import { Novel } from '@/lib/types';
import { Note } from '@common/schemas/notes.schema';
import {
  deleteField,
  DocumentData,
  FieldValue,
  setDoc,
  Transaction,
  UpdateData,
  updateDoc,
} from 'firebase/firestore';
import { BaseDao } from './base-dao';

/**
 * Specialized DAO for Notes.
 * Handles split storage structure (summaries and items) for notes.
 */
export class NotesDao extends BaseDao {
  private readonly SUMMARY_FIELDS = [
    'id', 'contentId', 'title', 'preview', 'folderId', 'tags', 'isFavorite', 'sourceImportSessionId', 'linkedEntityIds', 'createdAt', 'updatedAt'
  ];

  /**
   * Updates a note and automatically mirrors specified summary fields.
   */
  async updateNoteItem(
    userId: string,
    novel: Novel,
    noteId: string,
    updates: UpdateData<DocumentData> | FieldValue,
    transaction?: Transaction
  ): Promise<void> {
    const finalUpdates = { ...(updates as any), audit: { ...((updates as any).audit || {}), updatedAt: new Date().toISOString() } };
    await this.updateItem(userId, novel, 'narrative', 'notes', noteId, finalUpdates, transaction);
    await this.updateNoteSummary(userId, novel, noteId, finalUpdates, transaction);
  }

  /**
   * Sets (upserts) a note and mirrors summary fields.
   */
  async setNoteItem(
    userId: string,
    novel: Novel,
    note: Note,
    transaction?: Transaction
  ): Promise<void> {
    const finalItem = {
      ...note,
      audit: {
        ...note.audit,
        updatedAt: new Date().toISOString()
      }
    };
    await this.setItem(userId, novel, 'narrative', 'notes', finalItem, transaction);
    await this.updateNoteSummary(userId, novel, note.id, finalItem, transaction);
  }

  /**
   * Internal helper to update the mirror in the summaries bucket.
   */
  private async updateNoteSummary(
    userId: string,
    novel: Novel,
    noteId: string,
    data: DocumentData,
    transaction?: Transaction
  ): Promise<void> {
    const summaryUpdates: Record<string, unknown> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key)) {
        summaryUpdates[key] = value;
      }
      if (key === 'audit' && value?.updatedAt) {
        summaryUpdates.updatedAt = value.updatedAt;
      }
    });

    if (Object.keys(summaryUpdates).length === 0) return;

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', 'notes', noteId, novel.storageLayout);
    if (!fieldPath) return;

    const payload: Record<string, unknown> = {};
    Object.entries(summaryUpdates).forEach(([key, value]) => {
      payload[`${fieldPath}.${key}`] = value;
    });

    if (transaction) {
      transaction.update(ref, payload as UpdateData<DocumentData>);
    } else {
      try {
        await updateDoc(ref, payload as UpdateData<DocumentData>);
      } catch (e) {
        try {
          const nested = this.nestPayload(fieldPath, summaryUpdates);
          await setDoc(ref, nested, { merge: true });
        } catch (setErr) {
          console.warn(`Summary update failed for Note ${noteId}.`, setErr);
        }
      }
    }
  }

  /**
   * Deletes a note and removes its reference from summaries.
   */
  async deleteNoteItem(
    userId: string,
    novel: Novel,
    noteId: string,
    transaction?: Transaction
  ): Promise<void> {
    await this.deleteItem(userId, novel, 'narrative', 'notes', noteId, transaction);

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', 'notes', noteId, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: deleteField() };
      if (transaction) {
        transaction.update(ref, updates as UpdateData<DocumentData>);
      } else {
        await updateDoc(ref, updates as UpdateData<DocumentData>);
      }
    }
  }
}
